// Type Race rules with no DOM: which passage a day gets, how a run is scored, and the share
// line. Everything is worked out on the device; there is no server and no shared leaderboard.
import { PASSAGES } from "./typerace-passages.js";
import { mulberry32 } from "../shared/rng.js";

export const LAUNCH_DAY = "2026-10-26"; // Type Race #1
export const TIME_ZONE = "America/New_York";

// The calendar day in New York as "YYYY-MM-DD", so everyone shares one puzzle day.
export function dayKey(date = new Date()) {
	const parts = new Intl.DateTimeFormat("en-US", { timeZone: TIME_ZONE, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(date);
	const get = (t) => (parts.find((p) => p.type === t) || {}).value;
	return get("year") + "-" + get("month") + "-" + get("day");
}

const dayIndex = (key) => { const [y, m, d] = key.split("-").map(Number); return Math.round(Date.UTC(y, m - 1, d) / 86400000); };

// Launch day is #1, the next day #2. Days before launch come out as 0 or less.
export function dayNumber(key) { return dayIndex(key) - dayIndex(LAUNCH_DAY) + 1; }

// A fixed shuffle of the passages, so a passage never comes back until all of them have had a day.
// The seed is chosen so launch day opens with the tortoise and the hare.
function shuffledOrder(n) {
	const order = Array.from({ length: n }, (_, i) => i);
	const rand = mulberry32(20261081);
	for (let i = n - 1; i > 0; i--) { const j = Math.floor(rand() * (i + 1)); [order[i], order[j]] = [order[j], order[i]]; }
	return order;
}
const ORDER = shuffledOrder(PASSAGES.length);

export function passageIndexFor(key) {
	const n = ORDER.length;
	const slot = (((dayNumber(key) - 1) % n) + n) % n;
	return ORDER[slot];
}
export function passageFor(key) { return PASSAGES[passageIndexFor(key)]; }

// Words per minute counts only characters that ended up right (five characters to a word).
// Accuracy is about every key pressed, so a mistake still counts after it is fixed.
export function score({ target, typed, ms, keystrokes, errors }) {
	let correct = 0;
	const len = Math.min(target.length, typed.length);
	for (let i = 0; i < len; i++) if (typed[i] === target[i]) correct++;
	const minutes = Math.max(ms, 1000) / 60000;
	const wpm = Math.round(correct / 5 / minutes);
	const accuracy = keystrokes > 0 ? Math.round((100 * Math.max(0, keystrokes - errors)) / keystrokes) : 0;
	return { wpm, accuracy, correct, seconds: Math.round(ms / 100) / 10 };
}

export function shareLine(n, wpm, accuracy) {
	const label = n >= 1 ? "Type Race #" + n : "Type Race preview";
	return label + ": " + wpm + " wpm, " + accuracy + "% accuracy";
}

// What one edit to the box added. Only the part that changed is looked at (the text before and
// after it stayed the same), so fixing a letter a few words back counts that one key, not
// everything after it. `caret` is where the cursor ended up, used to place an insertion that
// could sit in more than one spot (typing "o" next to another "o"). Returns the keys pressed
// (one per inserted character) and how many of them did not match the passage where they landed.
export function countEdit(prev, next, target, caret = next.length) {
	let p = 0;
	const max = Math.min(prev.length, next.length);
	while (p < max && prev[p] === next[p]) p++;
	let s = 0;
	while (s < max - p && prev[prev.length - 1 - s] === next[next.length - 1 - s]) s++;
	const added = next.length - p - s;
	if (added <= 0) return { keystrokes: 0, errors: 0 };
	// A plain insertion can often sit in more than one spot; put it where the caret is when that fits.
	let start = p;
	const at = (Number.isInteger(caret) ? caret : next.length) - added;
	if (next.length - added === prev.length && at >= 0 && at !== p && next.slice(0, at) + next.slice(at + added) === prev) start = at;
	let errors = 0;
	for (let i = start; i < start + added; i++) if (next[i] !== target[i]) errors++;
	return { keystrokes: added, errors };
}
